
import { connect } from 'react-redux';
import { useEffect, useState } from 'react';

import { getAnalytics, logEvent } from "firebase/analytics";

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import { IosShareRounded } from '@mui/icons-material';
import { Check } from '@mui/icons-material';

import CloseFooter from './CloseFooter';
import StatusToast from './StatusToast';


import { getDisplaySpeakerData, speakerImageSrc } from './speakerHelper';
import ProfilePicture from './ProfilePicture';
import { fancyTimeFormat } from './timeHelper';

import { LABEL_DOMAIN } from '../constants/labels';
import { shareContent, SHARE_FAILURE_MESSAGE } from '../utils/shareContent';
import { ACCENT_COLOR } from '../constants/colors';

const ShareTrackDrawer = ({
    currentSection,
    curTime,
    close,
    open,
    track,
}) => {

    const analytics = getAnalytics();

    const [startAt, setStartAt] = useState(-1);

    const [currentMessage, setCurrentMessage] = useState(null);
    const currentSpeaker = currentMessage && track ? getDisplaySpeakerData(
        currentMessage.speakerId, track.speakers, currentMessage.anonymousDisplay,
    ) : null;
    const [startFromSelection, setStartFromSelection] = useState(0);

    const [copiedToastGeneration, setCopiedToastGeneration] = useState(0);
    const [shareError, setShareError] = useState('');

    useEffect(() => {
        if (open) {
            setStartAt(-1);
            setCopiedToastGeneration(0);
            setShareError('');
            setStartFromSelection(0);
            const currentTime = Number.isFinite(curTime) ? curTime : document.getElementById('audio')?.currentTime || 0;
            const transcript = track?.transcript ?? [];
            setCurrentMessage(null);
            for (let i = transcript.length - 1; i >= 0; i--) {
                const message = transcript[i];
                if (message.timeSeconds < currentTime) {
                    setCurrentMessage(message);
                    return;
                }
            }
        }
    }, [open, track, curTime])

    const startCurrentMessageClick = () => {
        setStartAt(Math.floor(currentMessage.timeSeconds));
        setStartFromSelection(2);
    }

    const startCurrentSectionClick = () => {
        const sectionStart = track.sectionInfos[currentSection].startTime
        setStartAt(Math.floor(sectionStart));
        setStartFromSelection(1);
    }

    const startFromBeginning = () => {
        setStartAt(-1);
        setStartFromSelection(0);
    }

    const closeCopied = () => {
        setCopiedToastGeneration(0);
        close();
    }

    const onShareLinkClick = async () => {
        let url = `https://${LABEL_DOMAIN}${track.pathname}`;
        if (startAt !== -1) {
            url += `?start=${startAt}s`;
        }
        setShareError('');
        setCopiedToastGeneration(0);
        logEvent(analytics, 'track_share_link');
        try {
            const result = await shareContent({ url });
            if (result === 'shared') close();
            else if (result === 'copied') setCopiedToastGeneration(generation => generation + 1);
        } catch (_) {
            setShareError(SHARE_FAILURE_MESSAGE);
        }
    }

    const getMessageText = (m) => {
        const messageText = [];
        for (let i = 0; i < m.message.length; i++) {
            messageText.push(m.message[i].text)
        }
        return messageText.join('');
    }

    return (
        <div>
            <StatusToast
                open={copiedToastGeneration > 0}
                instanceKey={copiedToastGeneration}
                onClose={closeCopied}
                message='Copied to clipboard'
            />
            <StatusToast tone='error' open={Boolean(shareError)} message={shareError} onClose={() => setShareError('')} />
            <Drawer
                PaperProps={{
                    sx: {
                        borderRadius: '18px 18px 0px 0px'
                    }
                }}
                anchor='bottom' open={open} onClose={close}>
                <Box
                    sx={{ width: 'auto' }}
                    role="presentation"
                >
                    <div className='horizontalCenter appDrawerSurface hasCloseFooter'>
                        {!track ? <></>
                            :
                            <div>
                                <div style={{ padding: '20px', background: 'var(--app-surface)', textAlign: 'center', marginBottom: '20px' }}>
                                    <div style={{ marginBottom: '10px', fontWeight: '600', fontSize: '20px' }}>
                                        Share Episode
                                    </div>
                                    <div style={{ color: 'var(--app-text-secondary)', fontSize: '13px' }}>
                                        https://{LABEL_DOMAIN}/track/{track.fullTrackId ?? track.id}{startAt !== -1 ? <span style={{ color: 'var(--app-text)', fontWeight: '600' }}>?start={startAt}s</span> : ''}
                                    </div>
                                </div>

                                <div style={{ padding: '0px 16px 20px' }} >
                                    {!track.aiFeatures ? <></> :
                                        <div>
                                            <div style={{ paddingBottom: '16px', fontSize: '18px', fontWeight: '500', textAlign: 'left' }}>
                                                Start from...
                                            </div>
                                            <div style={{ paddingBottom: '16px' }}>
                                                <button onClick={startFromBeginning} style={{ opacity: startFromSelection === 0 ? 1 : 0.6, width: '100%', textAlign: 'left', padding: '12px 16px', border: '1px solid', borderColor: startFromSelection === 0 ? 'var(--app-text)' : 'var(--app-separator)', borderRadius: '8px' }}>
                                                    <table style={{ width: '100%' }}>
                                                        <tbody>
                                                            <tr>
                                                                <td style={{ width: '100%', paddingRight: '20px' }}>
                                                                    <div style={{ fontWeight: startFromSelection === 0 ? '600' : '400', fontSize: '18px' }}>
                                                                        <span style={{ verticalAlign: 'middle' }}>
                                                                            Beginning
                                                                        </span>
                                                                        {startFromSelection === 0 ?
                                                                            <Check style={{ color: ACCENT_COLOR, fontSize: '24px', marginLeft: '6px', verticalAlign: 'middle' }} />
                                                                            :
                                                                            <></>
                                                                        }
                                                                    </div>
                                                                </td>
                                                                <td style={{ fontSize: '13px', color: 'var(--app-text-secondary)' }}>
                                                                    0:00
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </button>
                                            </div>
                                            {!currentSection ? <></> :
                                                <div style={{ paddingBottom: '16px' }}>
                                                    <button onClick={startCurrentSectionClick} className='clampSafeButton' style={{ opacity: startFromSelection === 1 ? 1 : 0.6, textAlign: 'left', width: '100%', padding: '12px 16px', border: '1px solid', borderColor: startFromSelection === 1 ? 'var(--app-text)' : 'var(--app-separator)', borderRadius: '8px' }}>
                                                        <table style={{ width: '100%' }}>
                                                            <tbody>
                                                                <tr>
                                                                    <td style={{ width: '100%', paddingRight: '20px' }}>
                                                                        <div style={{ fontWeight: startFromSelection === 1 ? '600' : '400', fontSize: '18px' }}>
                                                                            <span style={{ verticalAlign: 'middle' }}>
                                                                                Current Section
                                                                            </span>
                                                                            {startFromSelection === 1 ?
                                                                                <Check style={{ color: ACCENT_COLOR, fontSize: '24px', marginLeft: '6px', verticalAlign: 'middle' }} />
                                                                                :
                                                                                <></>
                                                                            }
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ fontSize: '13px', color: 'var(--app-text-secondary)' }}>
                                                                        {fancyTimeFormat(track.sectionInfos[currentSection].startTime)}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        <div style={{ marginTop: '6px' }}>
                                                            <div style={{ fontSize: '14px', fontWeight: '400' }}>
                                                                <span className='messageTextPreview'>
                                                                    {track.sectionInfos[currentSection].title}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </button>
                                                </div>
                                            }

                                            {!currentMessage ? <></> :
                                                <div style={{ paddingBottom: '30px' }}>
                                                    <button onClick={startCurrentMessageClick} className='clampSafeButton' style={{ opacity: startFromSelection === 2 ? 1 : 0.6, width: '100%', textAlign: 'left', padding: '12px 16px', border: '1px solid', borderColor: startFromSelection === 2 ? 'var(--app-text)' : 'var(--app-separator)', borderRadius: '8px' }}>
                                                        <table style={{ width: '100%' }}>
                                                            <tbody>
                                                                <tr>
                                                                    <td style={{ width: '100%', paddingRight: '20px' }}>
                                                                        <div style={{ fontWeight: startFromSelection === 2 ? '600' : '400', fontSize: '18px' }}>
                                                                            <span style={{ verticalAlign: 'middle' }}>
                                                                                Current Message
                                                                            </span>
                                                                            {startFromSelection === 2 ?
                                                                                <Check style={{ color: ACCENT_COLOR, fontSize: '24px', marginLeft: '6px', verticalAlign: 'middle' }} />
                                                                                :
                                                                                <></>
                                                                            }
                                                                        </div>
                                                                    </td>
                                                                    <td style={{ fontSize: '13px', color: 'var(--app-text-secondary)' }}>
                                                                        {fancyTimeFormat(currentMessage.timeSeconds)}
                                                                    </td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                        <div style={{ marginTop: '6px' }}>
                                                            <table>
                                                                <tbody>
                                                                    <tr>
                                                                        <td style={{ minWidth: '44px' }}>
                                                                            <ProfilePicture
                                                                                speakerId={currentSpeaker.id}
                                                                                imageSrc={speakerImageSrc(currentMessage.speakerId, track.speakers)}
                                                                                anonymousDisplay={currentSpeaker.anonymousDisplay}
                                                                                borderSize={0}
                                                                                size='34px' />
                                                                        </td>
                                                                        <td>
                                                                            <span className='messageTextPreview'>{getMessageText(currentMessage)}</span>
                                                                        </td>
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </button>
                                                </div>
                                            }
                                        </div>
                                    }

                                    <div>
                                        <button onClick={onShareLinkClick} style={{
                                            width: '100%',
                                            padding: '10px',
                                            background: 'var(--app-action)',
                                            color: 'var(--app-action-ink)',
                                            borderRadius: '8px',
                                            fontSize: '18px',
                                            fontWeight: '600'
                                        }}>
                                            <IosShareRounded style={{ verticalAlign: 'middle', marginRight: '10px' }} />
                                            <span style={{ verticalAlign: 'middle' }}>
                                                Share
                                            </span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        }
                        <CloseFooter onClick={close} />
                    </div>
                </Box>
            </Drawer>
        </div>
    );
};

const mapStateToProps = (state) => ({

})

const mapActionsToProps = {

}

export default connect(mapStateToProps, mapActionsToProps)(ShareTrackDrawer);
